import * as THREE from "three";
import { PLAYER_HEIGHT, lerp, lerpAngle, dampFactor } from "@craftgaming/shared";
import type { RemotePlayerState } from "../network/colyseus-network";
import { CLASS_DEFS, type ChampionClass } from "@craftgaming/shared";
import {
  createPlayerModel,
  colorsFromId,
  colorsFromClassColor,
  type PlayerModel,
} from "../rendering/player-model";

const SMOOTHING_LAMBDA = 12; // higher = snappier convergence to server position
const NAMETAG_HEIGHT = PLAYER_HEIGHT + 0.5;

type RemotePlayerEntity = {
  model: PlayerModel;
  nametag: THREE.Sprite;
  // Interpolation: current follows target (server sends ~20 updates/s)
  target: { x: number; y: number; z: number; yaw: number };
  current: { x: number; y: number; z: number; yaw: number };
};

export type RemotePlayers = {
  add: (state: RemotePlayerState) => void;
  updateTarget: (state: RemotePlayerState) => void;
  remove: (sessionId: string) => void;
  update: (dt: number) => void;
  count: () => number;
  /** Current (smoothed) horizontal positions — e.g. minimap markers. */
  getPositions: () => Array<{ x: number; z: number }>;
  /** Remote players as PvP targets (sessionId + smoothed 3D position). */
  getTargets: () => Array<{ sessionId: string; x: number; y: number; z: number }>;
  dispose: () => void;
};

function createNametag(username: string): THREE.Sprite {
  const canvas = document.createElement("canvas");
  canvas.width = 256;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = "bold 32px 'Segoe UI', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#ffffff";
    ctx.fillText(username.slice(0, 16), canvas.width / 2, canvas.height / 2);
  }
  const texture = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({ map: texture, depthTest: false });
  const sprite = new THREE.Sprite(material);
  sprite.scale.set(1.6, 0.4, 1);
  sprite.position.y = NAMETAG_HEIGHT;
  return sprite;
}

export function createRemotePlayers(scene: THREE.Scene): RemotePlayers {
  const entities = new Map<string, RemotePlayerEntity>();

  function add(state: RemotePlayerState): void {
    if (entities.has(state.sessionId)) return;

    // Same outfit the player themselves sees (class color, synced via
    // schema) — fallback to a hash color if the class is unknown
    const classDef = CLASS_DEFS[state.championClass as ChampionClass];
    const model = createPlayerModel(
      classDef ? colorsFromClassColor(classDef.color) : colorsFromId(state.sessionId)
    );
    const nametag = createNametag(state.username);
    model.group.add(nametag);
    model.group.position.set(state.x, state.y, state.z);
    model.group.rotation.y = state.yaw;
    scene.add(model.group);

    entities.set(state.sessionId, {
      model,
      nametag,
      target: { x: state.x, y: state.y, z: state.z, yaw: state.yaw },
      current: { x: state.x, y: state.y, z: state.z, yaw: state.yaw },
    });
  }

  function updateTarget(state: RemotePlayerState): void {
    const entity = entities.get(state.sessionId);
    if (!entity) return;
    entity.target.x = state.x;
    entity.target.y = state.y;
    entity.target.z = state.z;
    entity.target.yaw = state.yaw;
  }

  function remove(sessionId: string): void {
    const entity = entities.get(sessionId);
    if (!entity) return;
    scene.remove(entity.model.group);
    entity.nametag.material.map?.dispose();
    entity.nametag.material.dispose();
    entity.model.dispose();
    entities.delete(sessionId);
  }

  function update(dt: number): void {
    if (dt <= 0) return;
    const t = dampFactor(SMOOTHING_LAMBDA, dt);
    for (const entity of entities.values()) {
      const { current, target } = entity;
      const prevX = current.x;
      const prevZ = current.z;

      current.x = lerp(current.x, target.x, t);
      current.y = lerp(current.y, target.y, t);
      current.z = lerp(current.z, target.z, t);
      current.yaw = lerpAngle(current.yaw, target.yaw, t);

      entity.model.group.position.set(current.x, current.y, current.z);
      // Model faces -z; server yaw is the camera yaw
      entity.model.group.rotation.y = current.yaw;

      // Estimated horizontal speed drives the walk animation
      const dx = current.x - prevX;
      const dz = current.z - prevZ;
      const speed = Math.sqrt(dx * dx + dz * dz) / dt;
      entity.model.update(dt, speed);
    }
  }

  function dispose(): void {
    for (const sessionId of [...entities.keys()]) {
      remove(sessionId);
    }
  }

  function getPositions(): Array<{ x: number; z: number }> {
    const positions: Array<{ x: number; z: number }> = [];
    for (const entity of entities.values()) {
      positions.push({ x: entity.current.x, z: entity.current.z });
    }
    return positions;
  }

  function getTargets(): Array<{ sessionId: string; x: number; y: number; z: number }> {
    const targets: Array<{ sessionId: string; x: number; y: number; z: number }> = [];
    for (const [sessionId, entity] of entities) {
      targets.push({ sessionId, x: entity.current.x, y: entity.current.y, z: entity.current.z });
    }
    return targets;
  }

  return { add, updateTarget, remove, update, count: () => entities.size, getPositions, getTargets, dispose };
}
